import React, {useState} from 'react';
import {NotificationManager} from 'react-notifications';

import Button from './Button';
import Icon from './Icon';
import PrinterPickerDialog from './PrinterPickerDialog';
import * as api from '../lib/api';
import {getPrinters, printZpl, rememberPrinterUid} from '../lib/zebraPrint';
import {preferredPrinterUid, deviceLabel, describeError} from '../lib/zebraPrintUi';

/**
 * "Print to Zebra" action for a single data row. Renders the label server-side (via the API, identical
 * to the scale hot-path) and sends the ZPL to a printer through the local Zebra Browser Print app.
 *
 * When more than one printer is available a small chooser is shown (pre-selecting the last-used or
 * default printer); with exactly one printer it prints straight away. The chooser is portalled to the
 * document body so it sits above the row-detail side modal it is triggered from.
 */
function PrintToZebraControl({sheetId, rowId, credentials}) {
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null); // {devices, selectedUid} while the chooser is open

  const handleClick = async () => {
    setBusy(true);
    try {
      // Resolve the printer first: the label is rendered server-side scaled to the selected printer's
      // DPI (parsed from its Browser Print name), so we need the target before asking for the ZPL.
      const {devices, defaultUid} = await getPrinters();

      if (devices.length === 0) {
        NotificationManager.error(
          'No printers are set up in Browser Print on this computer. Add your printer in the Browser '
          + 'Print app, then try again.', 'No printers found');
        return;
      }

      if (devices.length === 1) {
        await renderAndSend(devices[0]);
        return;
      }

      setPicker({devices, selectedUid: preferredPrinterUid(devices, defaultUid)});
    }
    catch (err) {
      NotificationManager.error(describeError(err), 'Could not print');
    }
    finally {
      setBusy(false);
    }
  };

  const handleConfirmPrint = async () => {
    const device = picker.devices.find(d => d.uid === picker.selectedUid) || picker.devices[0];
    setPicker(null);
    await renderAndSend(device);
  };

  // Render the label scaled to this printer's DPI, then send it. deviceLabel(device) is the Browser
  // Print name the backend parses the DPI token from; an unrecognised name leaves the 203dpi default.
  const renderAndSend = async (device) => {
    setBusy(true);
    try {
      const {zpl} = await api.renderRowLabel(sheetId, rowId, credentials, deviceLabel(device));
      await printZpl(device, zpl);
      rememberPrinterUid(device.uid);
      NotificationManager.success(`Label sent to ${deviceLabel(device)}.`, 'Printed');
    }
    catch (err) {
      NotificationManager.error(describeError(err), 'Could not print');
    }
    finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button secondary type="button" disabled={busy} onClick={handleClick}>
        <Icon name="printer" /> {busy ? 'Printing...' : 'Print to Zebra'}
      </Button>

      {picker && (
        <PrinterPickerDialog
          devices={picker.devices}
          selectedUid={picker.selectedUid}
          onChange={selectedUid => setPicker(current => ({...current, selectedUid}))}
          onConfirm={handleConfirmPrint}
          onCancel={() => setPicker(null)}
        />
      )}
    </>
  );
}

export default PrintToZebraControl;
